"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthToken } from "@/hooks/use-auth-token";
import type { EffectCard } from "@/lib/core/schemas/effect-card";
import type { QueueItem } from "@/lib/core/schemas/encounter-state";
import type { SaveRoll } from "@/lib/core/schemas/save-roll.schema";
import logger from "@/lib/logger";
import type { PendingActionView } from "@/lib/systems/actions/read-view";
import type { PendingChoiceView } from "@/lib/systems/pending-choices/read-view";
import type { SkillCheckView } from "@/lib/systems/skill-checks/read-view";

export type EncounterDomain = {
  encounterActive: boolean;
  round: number;
  turnIndex: number;
  queue: QueueItem[];
  activeCombatantId: string | null;
  startEncounterToolCallId: string | null;
};

type GameStateStore = {
  campaignId: string;
  skillChecks: Map<string, SkillCheckView>;
  pendingActions: PendingActionView[];
  pendingChoices: PendingChoiceView[];
  saveRolls: SaveRoll[];
  effectCards: EffectCard[];
  encounter: EncounterDomain | null;
  loading: Set<string>;
  errors: Map<string, string>;
  invalidate: (domains: string[]) => void;
};

const GameStateContext = createContext<GameStateStore | null>(null);

export function useGameState(): GameStateStore {
  const ctx = useContext(GameStateContext);
  if (!ctx) {
    throw new Error("useGameState must be used within GameStateProvider");
  }
  return ctx;
}

export function GameStateProvider({
  campaignId,
  children,
}: {
  campaignId: string;
  children: React.ReactNode;
}) {
  const [skillChecks, setSkillChecks] = useState<Map<string, SkillCheckView>>(
    () => new Map()
  );
  const [pendingActions, setPendingActions] = useState<PendingActionView[]>([]);
  const [pendingChoices, setPendingChoices] = useState<PendingChoiceView[]>([]);
  const [saveRolls, setSaveRolls] = useState<SaveRoll[]>([]);
  const [effectCards, setEffectCards] = useState<EffectCard[]>([]);
  const [encounter, setEncounter] = useState<EncounterDomain | null>(null);
  const [loading, setLoading] = useState<Set<string>>(() => new Set());
  const [errors, setErrors] = useState<Map<string, string>>(() => new Map());

  const { fetchWithAuth } = useAuthToken();

  const inFlight = useRef<Set<string>>(new Set());
  const dirty = useRef<Set<string>>(new Set());
  const campaignIdRef = useRef(campaignId);
  campaignIdRef.current = campaignId;
  const encounterRef = useRef<EncounterDomain | null>(encounter);
  encounterRef.current = encounter;

  const fetchDomain = useCallback(
    async (domain: string) => {
      const fetchCampaignId = campaignId;
      inFlight.current.add(domain);
      setLoading((prev) => new Set(prev).add(domain));
      setErrors((prev) => {
        if (!prev.has(domain)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(domain);
        return next;
      });

      try {
        if (domain === "skill-checks") {
          const res = await fetchWithAuth(
            `/api/skill-checks?campaignId=${encodeURIComponent(fetchCampaignId)}`
          );
          if (!res.ok) {
            throw new Error(`Failed to fetch ${domain}: ${res.status}`);
          }
          // Discard result if campaignId changed while in flight
          if (campaignIdRef.current !== fetchCampaignId) {
            return;
          }
          const ack = await res.json();
          const checks = (ack.data ?? []) as SkillCheckView[];
          const map = new Map<string, SkillCheckView>();
          for (const check of checks) {
            map.set(check.entityId, check);
          }
          setSkillChecks(map);
        } else if (domain === "pending-actions") {
          const res = await fetchWithAuth(
            `/api/pending-actions?campaignId=${encodeURIComponent(fetchCampaignId)}`
          );
          if (!res.ok) {
            throw new Error(`Failed to fetch ${domain}: ${res.status}`);
          }
          // Discard result if campaignId changed while in flight
          if (campaignIdRef.current !== fetchCampaignId) {
            return;
          }
          const ack = await res.json();
          const actions = (ack.data ?? []) as PendingActionView[];
          setPendingActions(actions);
        } else if (domain === "pending-choices") {
          const res = await fetchWithAuth(
            `/api/pending-choices?campaignId=${encodeURIComponent(fetchCampaignId)}`
          );
          if (!res.ok) {
            throw new Error(`Failed to fetch ${domain}: ${res.status}`);
          }
          // Discard result if campaignId changed while in flight
          if (campaignIdRef.current !== fetchCampaignId) {
            return;
          }
          const ack = await res.json();
          const choices = (ack.data ?? []) as PendingChoiceView[];
          setPendingChoices(choices);
        } else if (domain === "effect-cards") {
          const res = await fetchWithAuth(
            `/api/effect-cards?campaignId=${encodeURIComponent(fetchCampaignId)}`
          );
          if (!res.ok) {
            throw new Error(`Failed to fetch ${domain}: ${res.status}`);
          }
          // Discard result if campaignId changed while in flight
          if (campaignIdRef.current !== fetchCampaignId) {
            return;
          }
          const body = await res.json();
          setEffectCards((body.cards ?? []) as EffectCard[]);
        } else if (domain === "encounter") {
          const res = await fetchWithAuth(
            `/api/campaigns/${encodeURIComponent(fetchCampaignId)}/encounter`
          );
          if (!res.ok) {
            throw new Error(`Failed to fetch ${domain}: ${res.status}`);
          }
          if (campaignIdRef.current !== fetchCampaignId) {
            return;
          }
          // Route returns ack.data directly (bespoke domain shape, not the
          // ACK envelope); mirrors effect-cards' { cards: [...] } pattern.
          const body = (await res.json()) as EncounterDomain | null;
          // Defense-in-depth: collapse only duplicate ordinary slots. Extra
          // turn slots intentionally repeat actorId and carry their own slot id.
          if (body && Array.isArray(body.queue)) {
            const seenOrdinaryActorIds = new Set<string>();
            const queue: QueueItem[] = [];
            let invalidQueue = false;
            for (const slot of [...body.queue].reverse()) {
              if (!slot || typeof slot.actorId !== "string") {
                invalidQueue = true;
                break;
              }
              if (slot.extraTurn) {
                queue.push(slot);
                continue;
              }
              if (seenOrdinaryActorIds.has(slot.actorId)) {
                continue;
              }
              seenOrdinaryActorIds.add(slot.actorId);
              queue.push(slot);
            }
            if (invalidQueue) {
              setEncounter(body);
            } else {
              setEncounter({ ...body, queue: queue.reverse() });
            }
          } else {
            setEncounter(body);
          }
        } else if (domain === "save-rolls") {
          const actorId = encounterRef.current?.activeCombatantId;
          if (!actorId) {
            // No active combatant ⇒ no turn-blocking ambient save can be
            // pending (the gate is encounter-scoped). Clear and skip the
            // fetch. The `finally` block still runs (clears loading/dirty).
            setSaveRolls([]);
            return;
          }
          const res = await fetchWithAuth(
            `/api/save-rolls?campaignId=${encodeURIComponent(
              fetchCampaignId
            )}&actorId=${encodeURIComponent(actorId)}`
          );
          if (!res.ok) {
            throw new Error(`Failed to fetch ${domain}: ${res.status}`);
          }
          // Discard result if campaignId changed while in flight
          if (campaignIdRef.current !== fetchCampaignId) {
            return;
          }
          const ack = await res.json();
          // Defense-in-depth: saveRolls is consumed via `.filter(...)` in the
          // pinned-card render, so guard against a malformed non-array ack.data
          // (mirrors the encounter branch's Array.isArray guard).
          const rolls = Array.isArray(ack.data) ? (ack.data as SaveRoll[]) : [];
          setSaveRolls(rolls);
        }
      } catch (err) {
        if (campaignIdRef.current !== fetchCampaignId) {
          return;
        }
        const message = err instanceof Error ? err.message : "Unknown error";
        logger.error({ err, domain }, "GameStateStore fetch failed");
        setErrors((prev) => {
          const next = new Map(prev);
          next.set(domain, message);
          return next;
        });
      } finally {
        inFlight.current.delete(domain);
        setLoading((prev) => {
          const next = new Set(prev);
          next.delete(domain);
          return next;
        });
        if (dirty.current.has(domain)) {
          dirty.current.delete(domain);
          fetchDomain(domain);
        }
      }
    },
    [campaignId, fetchWithAuth]
  );

  const invalidate = useCallback(
    (domains: string[]) => {
      for (const domain of domains) {
        if (inFlight.current.has(domain)) {
          dirty.current.add(domain);
          continue;
        }
        fetchDomain(domain);
      }
    },
    [fetchDomain]
  );

  // Mount-time hydration
  useEffect(() => {
    fetchDomain("skill-checks");
    fetchDomain("pending-actions");
    fetchDomain("pending-choices");
    fetchDomain("effect-cards");
    fetchDomain("encounter");
  }, [fetchDomain]);

  // Re-fetch the active combatant's pending ambient saves whenever the turn
  // advances (activeCombatantId changes) or the encounter first loads. This is
  // the PRIMARY refresh path for the turn-blocking save card and is correctly
  // ordered — it fires off the encounter STATE change, not the invalidation,
  // so it never reads a stale activeCombatantId.
  // biome-ignore lint/correctness/useExhaustiveDependencies: encounter?.activeCombatantId is an intentional trigger dependency — the effect body reads it via encounterRef inside fetchDomain, so re-running on every turn advance is the desired behavior
  useEffect(() => {
    fetchDomain("save-rolls");
  }, [fetchDomain, encounter?.activeCombatantId]);

  const value = useMemo(
    () => ({
      campaignId,
      skillChecks,
      pendingActions,
      pendingChoices,
      saveRolls,
      effectCards,
      encounter,
      loading,
      errors,
      invalidate,
    }),
    [
      campaignId,
      skillChecks,
      pendingActions,
      pendingChoices,
      saveRolls,
      effectCards,
      encounter,
      loading,
      errors,
      invalidate,
    ]
  );

  return (
    <GameStateContext.Provider value={value}>
      {children}
    </GameStateContext.Provider>
  );
}
